'use client';
import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import Papa from 'papaparse';
import { Investigador, VersionSelection } from './types/rankingTypes';
import { VersionSelector } from './VersionSelector';
import { RankingDisplay, UnidadMetric, CentroMetric, ColaboracionMetric } from './RankingDisplay';
import { UploadFiles } from './UploadFiles';

export const RankingInvestigadores = () => {
    const [view, setView] = useState<'UPLOAD' | 'SELECTION' | 'RANKING'>('UPLOAD');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedVersionInfo, setSelectedVersionInfo] = useState<VersionSelection | null>(null);
    const [tipoReporte, setTipoReporte] = useState('investigadores');
    const [selectedCargo, setCargo] = useState<string>('');
    const [id, setId] = useState<number>(0);

    // --- ESTADOS RAW ---
    const [rawInvestigadores, setRawInvestigadores] = useState<Investigador[]>([]);
    const [rawUnidades, setRawUnidades] = useState<any[]>([]);
    const [rawCentros, setRawCentros] = useState<any[]>([]);
    const [rawColaboracion, setRawColaboracion] = useState<any[]>([]);

    // --- ESTADOS FILTRADOS ---
    const [dataUnidades, setDataUnidades] = useState<UnidadMetric[]>([]);
    const [dataCentros, setDataCentros] = useState<CentroMetric[]>([]);
    const [dataColaboracion, setDataColaboracion] = useState<ColaboracionMetric[]>([]);

    // --- HELPERS DE PROCESAMIENTO ---
    const processAndSetData = (uni: any[], cen: any[], col: any[]) => {
        setRawUnidades(uni);
        setRawCentros(cen);
        setRawColaboracion(col);

        setView('SELECTION');
    };

    // --- MANEJO DE CARGA MANUAL (SIMULACIÓN) ---
    const handleDataLoaded = (inv: any[], uni: any[], cen: any[]) => {
        processAndSetData(inv, uni, cen);
    };

    // --- MANEJO DE CARGA AUTOMÁTICA (HISTÓRICO) ---
    const handleSkipToHistory = async () => {
        setLoading(true);
        setError(null);

        const fetchAndParse = (url: string, encoding: string) => {
            return new Promise<any[]>(async (resolve) => {
                try {
                    const response = await fetch(url);
                    if (!response.ok) return resolve([]);
                    const buffer = await response.arrayBuffer();

                    // Usamos el encoding dinámico
                    const decoder = new TextDecoder(encoding);
                    const csvText = decoder.decode(buffer).replace(/^\uFEFF/, '');

                    Papa.parse<any>(csvText, {
                        header: true, delimiter: ';', skipEmptyLines: true,
                        complete: (res) => resolve(res.data),
                        error: () => resolve([])
                    });
                } catch { resolve([]); }
            });
        };
        try {

            const [uni, cen, col] = await Promise.all([
                fetchAndParse('/ranking_unidades.csv', 'utf-8'),
                fetchAndParse('/ranking_centros.csv', 'utf-8'),
                fetchAndParse('/ranking_colaboracion.csv', 'windows-1252')
            ]);

            processAndSetData(uni, cen, col);

        } catch (e) {
            console.error(e);
            setError("Error al cargar los datos históricos.");
        } finally {
            setLoading(false);
        }
    };

    // --- LÓGICA DE FILTRADO ---
    const handleShowRanking = (selection: VersionSelection) => {
        setLoading(true);
        const anioNum = parseInt(selection.anio, 10);
        const cleanNum = (val: any) => parseFloat(String(val).replace(',', '.')) || 0;
        const fmt = (val: any) => String(val || '').replace('.', ',');
        const filterByVersion = (rows: any[]) => {
            if (rows.length > 0 && !rows[0].hasOwnProperty('anio')) return rows;
            return rows.filter(row =>
                (parseInt(row.anio, 10) || 0) === anioNum &&
                row.fechacreacion === selection.fecha &&
                row.autocitas === selection.autocitas
            );
        };

        // 2. UNIDADES
        const uniFiltered = filterByVersion(rawUnidades).map((row: any, index: number) => {
            const rawColab = cleanNum(row['International Collaboration (%)']);
            return {
                posicion: index + 1,
                nombre: row['Unidad'] || row['Unidades Académicas'] || '',
                metricaFinal: fmt(row['MÉTRICA FINAL']),
                fwci: fmt(row['Field-Weighted Citation Impact']),
                productividad: (row['Productividad científica']),
                horas: parseInt(row['Horas de investigación'] || '0', 10),
                produccion: parseInt(row['Producción científica'] || '0', 10),
                produccionColab: parseInt(row['Producción científica con colaboración internacional'] || '0', 10),
                colaboracion: (rawColab).toFixed(2) + '%',
                l: parseInt(row['L'] || '0', 10),
                l1: parseInt(row['L-1'] || '0', 10),
                p: parseInt(row['P'] || '0', 10),
                irn: fmt(row['IRN']),
                irnL1: fmt(row['IRN (L-1)']),
                irnL1Norm: row['IRN (L-1) Normalizado (MAX)'],
                pubColabNorm: fmt(row['Publicaciones con colaboración internacional Normalizado'])
            };
        });
        setDataUnidades(uniFiltered);
        // 3. CENTROS (Ahora con las claves corregidas por UTF-8)
        const cenFiltered = filterByVersion(rawCentros).map((row: any, index: number) => {
            const rawColab = cleanNum(row['International Collaboration (%)']);
            return {
                posicion: index + 1,
                nombre: row['Centros'] || '',
                tipo: row['Tipo'] || '',
                metricaFinal: (row['MÉTRICA FINAL']),
                fwci: fmt(row['Field-Weighted Citation Impact']),
                productividad: fmt(row['Productividad científica']),
                horas: parseInt(row['Horas de investigación'] || '0', 10),
                produccion: parseInt(row['Producción científica'] || '0', 10),
                produccionColab: parseInt(row['Producción científica con colaboración internacional'] || '0', 10),
                colaboracion: (rawColab * 100).toFixed(2) + '%',
                l: parseInt(row['L'] || '0', 10),
                l1: parseInt(row['L-1'] || '0', 10),
                p: parseInt(row['P'] || '0', 10),
                irn: fmt(row['IRN']),
                irnL1: fmt(row['IRN (L-1)']),
                irnL1Norm: row['IRN (L-1) Normalizado (MAX)'],
                pubColabNorm: fmt(row['Publicaciones con colaboración internacional Normalizado'])
            };
        });
        setDataCentros(cenFiltered);

        // 4. COLABORACIÓN
        const colFiltered = filterByVersion(rawColaboracion).map((row: any, index: number) => {
            const rawPorc = cleanNum(row['Porcentaje Internacional']);
            return {
                posicion: index + 1,
                nombre: row['Investigador'] || '',
                scopusId: row['Scopus ID'] || '',
                publicaciones: parseInt(row['Publicaciones'] || '0', 10),
                colaboracion: parseInt(row['Publicaciones con colaboración internacional'] || '0', 10),
                porcentaje: (rawPorc * 100).toFixed(2) + '%',
                l: parseInt(row['L'] || '0', 10),
                p: parseInt(row['P'] || '0', 10),
                lnP: String(row['LN(P)'] || '').replace('.', ','),
                irn: String(row['IRN'] || '').replace('.', ','),
                irnL1: String(row['IRN (L-1)'] || '').replace('.', ','),
                irnL1Normalizado: String(row['IRN (L-1) Normalizado (MAX)'] || '').replace('.', ','),
                colaboracionNormalizado: String(row['Publicaciones con colaboración internacional Normalizado'] || '').replace('.', ','),
                puntaje: String(row['INDICADOR FINAL'] || '').replace('.', ',')
            };
        });
        setDataColaboracion(colFiltered);

        setSelectedVersionInfo(selection);
        setView('RANKING');
        setLoading(false);
    };

    const handleBackToSelection = () => {
        setView('SELECTION');
        setDataUnidades([]);
        setDataCentros([]);
        setDataColaboracion([]);
        setSelectedVersionInfo(null);
    };

    if (view === 'UPLOAD') {
        return <UploadFiles
            onDataLoaded={handleDataLoaded}
            onSkip={handleSkipToHistory}
        />;
    }

    if (loading && rawInvestigadores.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    if (view === 'SELECTION') {
        return <VersionSelector
            setId={setId}
            setCargo={setCargo}
            setTipoReporte={setTipoReporte}
            rawdata={rawInvestigadores}
            onShowRanking={handleShowRanking}
            loading={loading}
            onBack={() => setView('UPLOAD')}
        />;
    }

    if (error) return <Typography color="error" sx={{ p: 4 }}>{error}</Typography>;

    return <RankingDisplay
        cargo={selectedCargo}
        id={id}
        tipo={tipoReporte}
        onBack={handleBackToSelection}
        anio={selectedVersionInfo?.anio || ''}
        fechaCreacion={selectedVersionInfo?.fecha || ''}
        autocitas={selectedVersionInfo?.autocitas || ''}
        isOficial={selectedVersionInfo?.isOficial || false}
    />;
};

export default RankingInvestigadores;